/**
 * 进度跟踪模块 - 自定义Hooks
 * Progress Tracking Module - Custom Hooks
 * 
 * 注意：已移除合同(Contract)相关Hook，所有数据直接与项目(Project)关联
 */
import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { 
  workItemService, 
  progressService, 
  additionService, 
  rectificationService 
} from '../services';
import type { 
  IWorkItem, 
  IProgressRecord, 
  IAddition, 
  IRectification 
} from '../types';
import { ProjectService } from '../../../services/projectService';
import { WorkerService } from '../../../services/workerService';
import type { Project } from '../../../types';
import type { Worker } from '../../../types';

/**
 * 项目列表Hook
 * 获取所有活跃项目用于筛选
 */
export function useProjects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ProjectService.getActiveProjects();
      if (response.success && response.data) {
        setProjects(response.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取项目列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return { projects, loading, refresh: fetchProjects };
}

/**
 * 负责人列表Hook
 * 获取具有"leader"或"admin"角色的工人列表
 */
export function useLeaders() {
  const [leaders, setLeaders] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLeaders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await WorkerService.getLeaders();
      if (response.success && response.data) {
        setLeaders(response.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取负责人列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaders();
  }, [fetchLeaders]);

  return { leaders, loading, refresh: fetchLeaders };
}

/**
 * 工程项树形结构Hook
 * 
 * @param params.projectId - 项目ID（必填）
 */
export function useWorkItemTree(params: {
  projectId?: string;
}) {
  const [tree, setTree] = useState<IWorkItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchTree = useCallback(async () => {
    if (!params.projectId) {
      setTree([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await workItemService.getTree({ projectId: params.projectId });
      if (response.success && response.data) {
        setTree(response.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取工程项树失败');
    } finally {
      setLoading(false);
    }
  }, [params.projectId]);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  return { tree, loading, refresh: fetchTree };
}

/**
 * 工程项列表Hook
 * 
 * @param params.projectId - 项目ID（主要筛选条件）
 */
export function useWorkItems(params?: {
  projectId?: string;
  status?: string;
  category?: string;
}) {
  const [workItems, setWorkItems] = useState<IWorkItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });

  const fetchWorkItems = useCallback(async (page = 1, limit = 50) => {
    if (!params?.projectId) {
      setWorkItems([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await workItemService.getList({ ...params, page, limit });
      if (response.success && response.data) {
        setWorkItems(response.data.list);
        setPagination(response.data.pagination);
      }
    } catch (error: any) {
      message.error(error.message || '获取工程项列表失败');
    } finally {
      setLoading(false);
    }
  }, [params?.projectId, params?.status, params?.category]);

  useEffect(() => {
    fetchWorkItems();
  }, [fetchWorkItems]);

  return { workItems, loading, pagination, refresh: fetchWorkItems };
}

/**
 * 进度记录Hook
 * 
 * @param params.projectId - 项目ID（主要筛选条件）
 * @param params.workItemId - 工程项ID
 */
export function useProgressRecords(params?: {
  workItemId?: string;
  projectId?: string;
}) {
  const [records, setRecords] = useState<IProgressRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  const fetchRecords = useCallback(async (page = 1, limit = 20) => {
    setLoading(true);
    try {
      const response = await progressService.getList({ ...params, page, limit });
      if (response.success && response.data) {
        setRecords(response.data.list);
        setPagination(response.data.pagination);
      }
    } catch (error: any) {
      message.error(error.message || '获取进度记录失败');
    } finally {
      setLoading(false);
    }
  }, [params?.workItemId, params?.projectId]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  return { records, loading, pagination, refresh: fetchRecords };
}

/**
 * 增项列表Hook（项目主导）
 */
export function useAdditions(params?: {
  projectId?: string;
  status?: string;
  reason?: string;
  executorId?: string;
  isSubcontracted?: boolean;
  subcontractorId?: string;
}) {
  const [additions, setAdditions] = useState<IAddition[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  const fetchAdditions = useCallback(async (page = 1, limit = 20) => {
    setLoading(true);
    try {
      const response = await additionService.getList({ ...params, page, limit });
      if (response.success && response.data) {
        setAdditions(response.data.list);
        setPagination(response.data.pagination);
      }
    } catch (error: any) {
      message.error(error.message || '获取增项列表失败');
    } finally {
      setLoading(false);
    }
  }, [params?.projectId, params?.status, params?.reason, params?.executorId, params?.isSubcontracted, params?.subcontractorId]);

  useEffect(() => {
    fetchAdditions();
  }, [fetchAdditions]);

  return { additions, loading, pagination, refresh: fetchAdditions };
}

/**
 * 增项统计Hook
 */
export function useAdditionStats(projectId?: string) {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await additionService.getStats(projectId);
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取增项统计失败');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, refresh: fetchStats };
}

/**
 * 整改列表Hook
 * 
 * @param params.projectId - 项目ID（主要筛选条件）
 */
export function useRectifications(params?: {
  projectId?: string;
  status?: string;
  severity?: string;
}) {
  const [rectifications, setRectifications] = useState<IRectification[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  const fetchRectifications = useCallback(async (page = 1, limit = 20) => {
    setLoading(true);
    try {
      const response = await rectificationService.getList({ ...params, page, limit });
      if (response.success && response.data) {
        setRectifications(response.data.list);
        setPagination(response.data.pagination);
      }
    } catch (error: any) {
      message.error(error.message || '获取整改列表失败');
    } finally {
      setLoading(false);
    }
  }, [params?.projectId, params?.status, params?.severity]);

  useEffect(() => {
    fetchRectifications();
  }, [fetchRectifications]);

  return { rectifications, loading, pagination, refresh: fetchRectifications };
}

/**
 * 增项跟踪日志Hook
 */
export function useAdditionLogs(additionId?: string) {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!additionId) { setLogs([]); return; }
    setLoading(true);
    try {
      const response = await additionService.getLogs(additionId);
      if (response.success && response.data) {
        setLogs(response.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取增项日志失败');
    } finally {
      setLoading(false);
    }
  }, [additionId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return { logs, loading, refresh: fetchLogs };
}

/**
 * 待处理整改Hook
 */
export function usePendingRectifications() {
  const [rectifications, setRectifications] = useState<IRectification[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rectificationService.getPending();
      if (response.success && response.data) {
        setRectifications(response.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取待处理整改失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  return { rectifications, loading, refresh: fetchPending };
}
